import {
	CustomFieldRow,
	UpdateCustomDetail,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/types.ts';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { ActionIcon } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

type Handlers<TField> = {
	onEdit: (field: TField) => void;
	onToggleChecked: (args: UpdateCustomDetail) => void;
};

type GetCustomFieldsTableColumnsParams<TField> = {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	handlers: Handlers<TField>;
	isLoading: boolean;
	hasPermission: boolean;
};

export const getCustomFieldsTableColumns = <TField extends CustomFieldRow>({
	tMipManage,
	handlers,
	isLoading,
	hasPermission,
}: GetCustomFieldsTableColumnsParams<TField>): ExtendedColumnDef<TField>[] => {
	const { onEdit, onToggleChecked } = handlers;

	const baseColumns: ExtendedColumnDef<TField>[] = [
		createTextColumn<TField>({
			headerText: tMipManage('investor_data.custom_fields.table.name'),
			columnLabel: tMipManage('investor_data.custom_fields.table.name'),
			accessorKey: 'name',
			enableGlobalFilter: false,
			showFilter: false,
		}),
		createTextColumn<TField>({
			id: 'instruction',
			headerText: tMipManage('investor_data.custom_fields.table.description'),
			columnLabel: tMipManage('investor_data.custom_fields.table.description'),
			accessorKey: 'instruction',
			enableGlobalFilter: false,
			showFilter: false,
		}),
		{
			id: 'status',
			header: tMipManage('investor_data.custom_fields.table.status'),
			accessorFn: (row) => row.isChecked,
			enableSorting: false,
			enableColumnFilter: false,
			size: 140,
			cell: ({ row }) => {
				const field = row.original;
				return (
					<AdvancedSwitch
						selectedValue={field.isChecked}
						onChange={() =>
							onToggleChecked({
								customDetailId: field.id,
								name: field.name,
								instruction: field.instruction || '',
								isChecked: !field.isChecked,
							})
						}
						disabled={isLoading || !hasPermission}
					/>
				);
			},
		},
	];

	if (hasPermission) {
		baseColumns.push({
			id: 'actions',
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			size: 80,
			cell: ({ row }) => (
				<ActionIcon onClick={() => onEdit(row.original)} size="lg">
					<Icon variant="pencil" size="lg" color="black" />
				</ActionIcon>
			),
		});
	}

	return baseColumns;
};
